<template>
  <div class="department-staff-select">
    <el-popover
      ref="popRef"
      placement="bottom-start"
      trigger="click"
      width="240"
      @hide="popoverVisible = false"
      @show="popoverVisible = true"
    >
      <div class="department-staff-select-tabs">
        <div class="tab" :class="{ 'active-tab': currentTab === 1 }" @click="switchTab(1)">
          <i class="icon-user-3-line t-iconfont"></i>选成员
        </div>
        <div class="tab" :class="{ 'active-tab': currentTab === 2 }" @click="switchTab(2)">
          <i class="icon-organization-chart t-iconfont"></i>选部门
        </div>
      </div>
      <div v-if="currentTab === 2" class="department-staff-select-content">
        <el-input
          v-model="filterValue"
          class="node-filter-input"
          clearable
          placeholder="搜索部门"
          :prefix-icon="Search"
          @input="nodeFilterChangeHandler"
        />

        <el-scrollbar style="height: calc(100% - 39px); margin-top: 12px">
          <el-tree
            ref="treeRef"
            check-on-click-node
            :data="departmentList"
            :default-expanded-keys="defaultExpandedKeys"
            empty-text="未找到相关搜索结果"
            :expand-on-click-node="false"
            :filter-node-method="filterDepartNode"
            highlight-current
            node-key="pid"
            :props="defaultProps"
            @node-click="nodeClick"
          >
            <template #default="{ data }">
              <span class="ai-depart-node-name">
                <template
                  v-for="(segment, index) in getDepartmentNameSegments(data.text)"
                  :key="index"
                >
                  <span :class="{ 'keyword-highlight': segment.highlighted }">{{
                    segment.text
                  }}</span>
                </template>
              </span>
            </template>
          </el-tree>
        </el-scrollbar>
      </div>
      <div v-else class="department-staff-select-content">
        <el-input
          v-model="name"
          clearable
          placeholder="搜索成员"
          :prefix-icon="Search"
          @clear="handleClearSearch"
          @input="handleSearchInput"
        >
        </el-input>
        <div v-if="userList.length" class="user-select-content" :style="{ height: '260px' }">
          <el-scrollbar v-loading="searchLoading" style="height: 100%">
            <div
              v-for="item in userList"
              :key="item.id"
              class="user-item"
              @click="handleSelectUser(item)"
            >
              <img
                v-if="item.avatar && !item.avatar.includes('default.jpg')"
                alt=""
                class="avatar"
                :src="item.avatar"
              />
              <img v-else alt="" class="avatar" :src="userDefault" />
              <span>
                <template v-for="(segment, index) in getUserNameSegments(item.name)" :key="index">
                  <span :class="{ 'keyword-highlight': segment.highlighted }">{{
                    segment.text
                  }}</span>
                </template>
              </span>
            </div>
          </el-scrollbar>
        </div>
        <div v-else v-loading="searchLoading" class="user-select-empty">未找到相关搜索结果</div>
      </div>
      <template #reference>
        <div
          class="department-staff-select-box"
          :class="{ 'is-active': popoverVisible, 'has-value': Boolean(showText) }"
        >
          <span v-if="showText">{{ showText }}</span>
          <span v-else class="empty">{{ props.placeholder }}</span>
          <div class="icon">
            <i
              v-if="showText"
              class="clear-icon icon-close-circle-line t-iconfont"
              @click.stop="handleClear"
            />
            <i v-else class="arrow-icon icon-arrow-down-s-line t-iconfont" />
          </div>
        </div>
      </template>
    </el-popover>
  </div>
</template>

<script setup>
import { getDepartmentlist, geuUserList } from '@/api/ai-board'
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { Search } from '@element-plus/icons-vue'
import xbb from '@xbb/xbb-utils'
import userDefault from '@/assets/user-default.png'

defineOptions({
  name: 'DepartmentSelect'
})

const props = defineProps({
  value: {
    type: String,
    default: ''
  },
  departmentStaffSelectTabType: {
    type: Number,
    default: 2
  },
  placeholder: {
    type: String,
    default: '选择负责人或部门'
  }
})

const emits = defineEmits(['update:value', 'typeChange', 'selectLabel', 'clear'])

const treeRef = ref(null)
const popRef = ref(null)
const popoverVisible = ref(false)

const currentTab = ref(props.departmentStaffSelectTabType)
const defaultExpandedKeys = ref([])
const filterValue = ref('')
const defaultProps = ref({
  children: 'nodes',
  label: 'text'
})
const showText = ref('')
const departmentList = ref([])

const filterDepartNode = (value, data) => {
  if (!value) return true
  return data.text.indexOf(value) !== -1
}
const nodeFilterChangeHandler = (val) => {
  treeRef.value.filter(val)
}

const getHighlightSegments = (text = '', keyword = '') => {
  const sourceText = String(text ?? '')
  const trimmedKeyword = keyword?.trim()
  if (!trimmedKeyword) {
    return [{ text: sourceText, highlighted: false }]
  }

  const segments = []
  let startIndex = 0

  while (startIndex < sourceText.length) {
    const matchIndex = sourceText.indexOf(trimmedKeyword, startIndex)

    if (matchIndex === -1) {
      segments.push({
        text: sourceText.slice(startIndex),
        highlighted: false
      })
      break
    }

    if (matchIndex > startIndex) {
      segments.push({
        text: sourceText.slice(startIndex, matchIndex),
        highlighted: false
      })
    }

    segments.push({
      text: sourceText.slice(matchIndex, matchIndex + trimmedKeyword.length),
      highlighted: true
    })
    startIndex = matchIndex + trimmedKeyword.length
  }

  return segments.length ? segments : [{ text: sourceText, highlighted: false }]
}

const getUserNameSegments = (userName = '') => {
  return getHighlightSegments(userName, name.value)
}

const getDepartmentNameSegments = (departmentName = '') => {
  return getHighlightSegments(departmentName, filterValue.value)
}

// 节点点击事件
const nodeClick = (node) => {
  const { pid, text, disabled } = node
  if (disabled) return
  showText.value = text

  emits('typeChange', 2)
  emits('update:value', pid)
  emits('selectLabel', text)
  popRef.value.hide()
}

onMounted(() => {
  getUserList()
  getDepartmentlist({
    assistantAlias: 'meeting'
  })
    .then((res) => {
      departmentList.value = res.result.departmentTree
      defaultExpandedKeys.value = [departmentList.value[0].pid]
      departmentList.value[0].text = '全部'
    })
    .catch((err) => {
      console.log(err)
    })
})

onBeforeUnmount(() => {
  debouncedSearch.cancel?.()
})

// 成员选择

const searchLoading = ref(false)
const userList = ref([])
const name = ref('')

const getUserList = (keyword = '') => {
  searchLoading.value = true
  const params = {
    pageSize: 20,
    page: 1,
    nameLike: keyword?.trim()
  }
  geuUserList(params)
    .then((res) => {
      userList.value = res.result.userList
    })
    .catch((err) => {
      console.log(err)
    })
    .finally(() => {
      searchLoading.value = false
    })
}

const debouncedSearch = xbb.debounce(() => {
  getUserList(name.value)
}, 400)

const handleSelectUser = (item) => {
  emits('typeChange', 1)
  emits('update:value', item.userId)
  emits('selectLabel', item.name)
  nextTick(() => {
    showText.value = item.name
    popRef.value.hide()
  })
}

const handleClearSearch = () => {
  debouncedSearch.cancel?.()
  name.value = ''
  getUserList()
}

const handleClear = () => {
  debouncedSearch.cancel?.()
  showText.value = ''
  name.value = ''
  filterValue.value = ''
  getUserList()
  emits('update:value', '')
  emits('selectLabel', '')
  emits('clear')
  popRef.value?.hide()
}

const handleSearchInput = () => {
  searchLoading.value = true
  debouncedSearch()
}

function switchTab(val) {
  currentTab.value = val
}

watch(
  () => props.departmentStaffSelectTabType,
  (val) => {
    currentTab.value = val
  }
)

watch(
  () => props.value,
  (val) => {
    if (!val) {
      showText.value = ''
    }
  }
)
</script>

<style lang="scss" scoped>
:deep(.el-tree__empty-text) {
  width: 100%;
}
:deep(.ai-depart-node-name) {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}
.department-staff-select-tabs {
  display: flex;
  align-items: center;
  height: 32px;
  background: #f7f8fa;
  color: #969799;
  border-radius: 4px;
  padding: 2px;
  margin-bottom: 12px;
  .tab {
    height: 28px;
    line-height: 28px;
    flex: 1;
    text-align: center;
    cursor: pointer;
    .t-iconfont {
      margin-right: 8px;
    }
  }
  .active-tab {
    background-color: #fff;
    border-radius: 4px;
  }
}
.department-staff-select-content {
  height: 300px;

  :deep(.el-tree-node__label) {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

:deep(.keyword-highlight) {
  color: #7a52f4;
  font-weight: 500;
}

.user-select-content {
  margin-top: 8px;
  .user-item {
    display: flex;
    align-items: center;
    padding: 4px 0;
    .avatar {
      flex: 0 0 auto;
      margin-right: 8px;
    }
    img {
      width: 24px;
      height: 24px;
      border-radius: 4px;
    }
    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  .user-item:hover {
    cursor: pointer;
    background: #f7f8fa;
  }
}
.user-select-empty {
  font-family: PingFang SC;
  font-size: 14px;
  line-height: 20px;
  color: #969799;
  // 兼容一下element的树选择器
  min-height: 72px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.department-staff-select-box {
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 180px;
  height: 32px;
  padding: 0 11px;
  cursor: pointer;
  background: #ffffff;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;

  &:hover {
    border-color: #c0c4cc;
  }

  &.is-active {
    border-color: var(--el-color-primary);
  }

  span {
    flex: 1;
    overflow: hidden;
    font-size: 14px;
    line-height: 20px;
    color: #606266;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .empty {
    color: var(--el-text-color-placeholder);
  }

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;

    i {
      font-size: 14px;
      color: #a8abb2;
    }

    .clear-icon {
      cursor: pointer;
    }
  }

  .clear-icon {
    opacity: 0;
    transition: opacity 0.2s;
  }

  .arrow-icon {
    opacity: 1;
    transition: opacity 0.2s;
  }

  &.has-value:hover {
    .clear-icon {
      opacity: 1;
    }

    .arrow-icon {
      opacity: 0;
    }
  }
}
</style>
